/**
 * GPS坐标转换工具
 * WGS-84 <-> GCJ-02 (火星坐标系)
 * 用于高德、腾讯、谷歌中国地图
 */

/**
 * 判断是否在中国境外（境外不需要转换）
 * @param {number} lat - 纬度
 * @param {number} lng - 经度
 * @returns {boolean} 是否在中国境外
 */
function outOfChina(lat, lng) {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

/**
 * 纬度转换辅助函数
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */
function transformLat(x, y) {
  let ret =
    -100.0 +
    2.0 * x +
    3.0 * y +
    0.2 * y * y +
    0.1 * x * y +
    0.2 * Math.sqrt(Math.abs(x));
  ret +=
    ((20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(y * Math.PI) + 40.0 * Math.sin((y / 3.0) * Math.PI)) *
      2.0) /
    3.0;
  ret +=
    ((160.0 * Math.sin((y / 12.0) * Math.PI) +
      320 * Math.sin((y * Math.PI) / 30.0)) *
      2.0) /
    3.0;
  return ret;
}

/**
 * 经度转换辅助函数
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */
function transformLng(x, y) {
  let ret =
    300.0 +
    x +
    2.0 * y +
    0.1 * x * x +
    0.1 * x * y +
    0.1 * Math.sqrt(Math.abs(x));
  ret +=
    ((20.0 * Math.sin(6.0 * x * Math.PI) + 20.0 * Math.sin(2.0 * x * Math.PI)) *
      2.0) /
    3.0;
  ret +=
    ((20.0 * Math.sin(x * Math.PI) + 40.0 * Math.sin((x / 3.0) * Math.PI)) *
      2.0) /
    3.0;
  ret +=
    ((150.0 * Math.sin((x / 12.0) * Math.PI) +
      300.0 * Math.sin((x / 30.0) * Math.PI)) *
      2.0) /
    3.0;
  return ret;
}

/**
 * WGS-84 转 GCJ-02（火星坐标系）
 * @param {number} wgsLng - WGS-84 经度
 * @param {number} wgsLat - WGS-84 纬度
 * @returns {[number, number]} - [GCJ-02经度, GCJ-02纬度]
 */
function wgs84ToGcj02(wgsLng, wgsLat) {
  if (outOfChina(wgsLat, wgsLng)) {
    return [wgsLng, wgsLat];
  }

  let dLat = transformLat(wgsLng - 105.0, wgsLat - 35.0);
  let dLng = transformLng(wgsLng - 105.0, wgsLat - 35.0);

  const radLat = (wgsLat / 180.0) * Math.PI;
  let magic = Math.sin(radLat);
  magic = 1 - 0.00669342162296594323 * magic * magic;
  const sqrtMagic = Math.sqrt(magic);

  dLat =
    (dLat * 180.0) /
    (((6378245.0 * (1 - 0.00669342162296594323)) / (magic * sqrtMagic)) *
      Math.PI);
  dLng =
    (dLng * 180.0) / ((6378245.0 / sqrtMagic) * Math.cos(radLat) * Math.PI);

  const gcjLat = wgsLat + dLat;
  const gcjLng = wgsLng + dLng;

  return [gcjLng, gcjLat];
}

/**
 * GCJ-02 转 WGS-84（逆向转换，精度约1-2米）
 * @param {number} gcjLng - GCJ-02 经度
 * @param {number} gcjLat - GCJ-02 纬度
 * @returns {[number, number]} - [WGS-84经度, WGS-84纬度]
 */
function gcj02ToWgs84(gcjLng, gcjLat) {
  if (outOfChina(gcjLat, gcjLng)) {
    return [gcjLng, gcjLat];
  }

  const [lng, lat] = wgs84ToGcj02(gcjLng, gcjLat);
  return [2 * gcjLng - lng, 2 * gcjLat - lat];
}

/**
 * 批量转换 WGS-84 坐标数组为 GCJ-02
 * @param {Array} path - 原始坐标数组，支持多种格式
 * @param {string} format - 输入格式: 'point' (默认, [[lng,lat],...]), 'object' ([{lng,lat},...])
 * @returns {Array} 转换后的坐标数组（保持原格式）
 */
function batchWgs84ToGcj02(path, format = "point") {
  if (!path || !path.length) return [];

  if (format === "object") {
    return path.map(point => {
      const [lng, lat] = wgs84ToGcj02(point.longitude, point.latitude);
      return { ...point, longitude: lng, latitude: lat };
    });
  }

  // 默认 point 格式
  return path.map(point => {
    const [lng, lat] = point;
    return wgs84ToGcj02(lng, lat);
  });
}

/**
 * 批量转换 GCJ-02 坐标数组为 WGS-84
 * @param {Array} path - GCJ-02坐标数组
 * @param {string} format - 输入格式: 'point' (默认), 'object'
 * @returns {Array} 转换后的坐标数组
 */
function batchGcj02ToWgs84(path, format = "point") {
  if (!path || !path.length) return [];

  if (format === "object") {
    return path.map(point => {
      const [lng, lat] = gcj02ToWgs84(point.longitude, point.latitude);
      return { ...point, longitude: lng, latitude: lat };
    });
  }

  return path.map(point => {
    const [lng, lat] = point;
    return gcj02ToWgs84(lng, lat);
  });
}

// 导出模块
export default {
  wgs84ToGcj02,
  gcj02ToWgs84,
  batchWgs84ToGcj02,
  batchGcj02ToWgs84,
  outOfChina
};



// WEBPACK FOOTER //
// ./src/utils/gpsConverter.js